/**
 * Subidas multipart al backend de Dashbo.
 *
 * `APIClient` serializa el body con `JSON.stringify` y fija `Content-Type: application/json`, así
 * que no sirve para mandar un archivo. Los dos precedentes del repo (`support_chat/supportChatApi`
 * y `feedback_widget/feedbackApi`) calcaban los headers a mano; esto es la extracción que ambos
 * anotaban como deuda, ahora que hay un tercer call site (la subida de material a la Memoria).
 *
 * Lo que vive acá:
 *  - `buildMultipartHeaders()` — Token + `X-Dashbo-Client`, **sin** `Content-Type`: lo pone el
 *    navegador con el boundary. Setearlo a mano rompe el parseo del lado de Django.
 *  - `normalizeMultipartResponse()` — el MISMO shape que `APIClient.handleResponse`, para que un
 *    caller lea `status`, `reason_code`, `http_status` y `raw_body` igual que en un POST JSON.
 *  - `uploadWithProgress()` — sobre `XMLHttpRequest` y no `fetch`, porque es la única forma de tener
 *    `upload.onprogress` (y `abort()` viene gratis). Un PDF de 20 MB en una conexión lenta son
 *    decenas de segundos: sin barra, el usuario recarga la página a mitad de camino.
 *
 * Ninguna función rechaza por red: siempre resuelve un objeto con `status`.
 */
import * as Sentry from '@sentry/react';

import {LocalStorage} from './LocalStorage';
import {DASHBO_CLIENT_HEADER, DASHBO_CLIENT_HEADER_VALUE} from './APIClient';

const GENERIC_ERROR_MESSAGE = 'Se ha producido un error interno. Si el error persiste, por favor contacte al equipo de Dashbo.';

export const NETWORK_ERROR_RESPONSE = {
    status: 'NETWORK_ERROR',
    error_code: 'NETWORK_ERROR',
    error_message: '',
};

export const ABORTED_RESPONSE = {
    status: 'ABORTED',
    error_code: 'ABORTED',
    error_message: '',
};

// Techo de una subida. Generoso a propósito: 20 MB por una conexión mala son varios minutos y
// cortar antes sería peor que esperar. Lo que cierra es el caso del socket colgado, donde sin
// timeout la promesa no resuelve nunca.
export const UPLOAD_TIMEOUT_MS = 5 * 60 * 1000;

export function buildMultipartHeaders() {
    const headers = new Headers();
    const token = LocalStorage.fetchUserToken();
    if (token) headers.append('Authorization', 'Token ' + token);
    headers.set(DASHBO_CLIENT_HEADER, DASHBO_CLIENT_HEADER_VALUE);
    return headers;
}

function parseJson(text) {
    if (!text) return null;
    try {
        return JSON.parse(text);
    } catch (_) {
        return null;
    }
}

/**
 * Interpreta `(status HTTP, body crudo)` al shape de `APIClient`.
 *
 * Tres códigos tienen trato propio porque pueden llegar SIN el JSON del backend:
 *  - 413: lo puede cortar el proxy antes de Django (HTML o body vacío) → `reason_code: TOO_LARGE`.
 *  - 429: throttle DRF (`{detail}`) → `RATE_LIMITED`.
 *  - 401: token inválido (logout en otra pestaña) → `UNAUTHORIZED`, sin reintento.
 */
export function normalizeMultipartResponse(httpStatus, bodyText) {
    const body = parseJson(bodyText);
    // `status: 0` es lo que reporta XHR cuando la conexión se cortó, la bloqueó CORS o el usuario
    // perdió la red: no hay respuesta que interpretar. Sin esto caía al error genérico y el usuario
    // leía «error interno» para un problema que es suyo y que se arregla reintentando.
    if (!httpStatus) return {...NETWORK_ERROR_RESPONSE};
    if (httpStatus >= 200 && httpStatus < 300) {
        return body || {...NETWORK_ERROR_RESPONSE};
    }
    const base = {
        status: (body && body.status) || 'ERROR',
        error_message: (body && (body.detail || body.message || body.error_message)) || GENERIC_ERROR_MESSAGE,
        errors: (body && body.errors) || [],
        http_status: httpStatus,
        raw_body: body,
    };
    if (httpStatus === 413) {
        return {...base, error_code: 'TOO_LARGE', reason_code: (body && body.reason_code) || 'TOO_LARGE'};
    }
    if (httpStatus === 429) {
        return {...base, error_code: 'RATE_LIMITED', reason_code: body && body.reason_code};
    }
    if (httpStatus === 401) {
        return {...base, error_code: 'UNAUTHORIZED', reason_code: body && body.reason_code};
    }
    if (!body) {
        return {...base, error_code: httpStatus};
    }
    return {
        ...base,
        error_code: body.error_code || body.status || 'ERROR',
        reason_code: body.reason_code,
    };
}

/**
 * POST multipart con progreso de subida.
 *
 * `onProgress(fraction)` recibe 0..1 sólo cuando el navegador conoce el total (`lengthComputable`).
 * `signal` (un `AbortSignal`) cancela: resuelve `{status: 'ABORTED'}`, sin toast ni Sentry — es una
 * decisión del usuario, no un error.
 */
export function uploadWithProgress(url, formData, {onProgress, signal, extraHeaders = {}} = {}) {
    return new Promise((resolve) => {
        let xhr;
        try {
            xhr = new XMLHttpRequest();
            xhr.open('POST', url, true);
            buildMultipartHeaders().forEach((value, name) => xhr.setRequestHeader(name, value));
            Object.entries(extraHeaders).forEach(([name, value]) => xhr.setRequestHeader(name, value));
        } catch (error) {
            Sentry.captureException(error);
            resolve({...NETWORK_ERROR_RESPONSE});
            return;
        }

        if (xhr.upload && typeof onProgress === 'function') {
            xhr.upload.onprogress = (event) => {
                if (!event.lengthComputable || !event.total) return;
                onProgress(Math.min(1, event.loaded / event.total));
            };
        }
        // Sin timeout (el default de XHR es 0 = infinito), un socket que se cuelga a mitad de la
        // subida deja la promesa sin resolver PARA SIEMPRE: el contador de envíos en vuelo no baja,
        // el botón queda deshabilitado y la única salida es recargar la página.
        xhr.timeout = UPLOAD_TIMEOUT_MS;
        xhr.ontimeout = () => resolve({...NETWORK_ERROR_RESPONSE});
        xhr.onload = () => resolve(normalizeMultipartResponse(xhr.status, xhr.responseText));
        xhr.onerror = () => {
            Sentry.captureException(new Error(`Multipart upload failed: ${url}`));
            resolve({...NETWORK_ERROR_RESPONSE});
        };
        xhr.onabort = () => resolve({...ABORTED_RESPONSE});

        if (signal) {
            if (signal.aborted) {
                resolve({...ABORTED_RESPONSE});
                return;
            }
            signal.addEventListener('abort', () => xhr.abort(), {once: true});
        }

        try {
            xhr.send(formData);
        } catch (error) {
            Sentry.captureException(error);
            resolve({...NETWORK_ERROR_RESPONSE});
        }
    });
}
